// import { sendGA4EventWithMarketing, sendGA4PageViewWithMarketing } from '@shared/marketing/ga4'
import { getLogger } from '@logtape/logtape'

import {
  extractMarketingParams,
  getMarketingParamsWithSource,
  sendGA4EventWithMarketing,
  sendGA4PageViewWithMarketing,
} from '../marketing'
import { ANALYTICS_EVENTS } from './events'

const logger = getLogger(['web', 'analytics'])

/**
 * Analytics module for tracking user interactions and behavior
 * Abstraction layer over GA4 and internal analytics
 */

export * from './events'
export * from './hooks'
export * from './types'

type TrackEventOptions = {
  /**
   * Optional user identifier (anonymized)
   */
  userId?: string

  /**
   * Additional event properties/dimensions
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

/**
 * Track an analytics event
 *
 * @param eventName The name of the event to track
 * @param options Additional event properties and user context
 */
export function trackEvent(eventName: string, options: TrackEventOptions = {}) {
  try {
    // Send to GA4
    sendGA4EventWithMarketing(eventName, options)

    // Log event in development for debugging
    if (process.env.NODE_ENV !== 'production') {
      logger.debug('analytics-event', { eventName, options })
    }

    // Here we could add additional analytics providers if needed
    // For example, sending to an internal analytics service
  } catch (error) {
    // Never crash the app due to analytics failures
    logger.error('analytics-error', { error })
  }
}

/**
 * Track a page view event
 *
 * @param pageName The name/path of the page being viewed
 * @param options Additional page and user context properties
 */
export function trackPageView(pageName: string, options: TrackEventOptions = {}) {
  const pagePath = typeof window !== 'undefined' ? window.location.pathname : undefined

  // Track as standard event
  trackEvent(ANALYTICS_EVENTS.PAGE_VIEWED, {
    page_name: pageName,
    page_path: pagePath,
    ...options,
  })

  // Also send as pageview to GA4
  if (pagePath) {
    sendGA4PageViewWithMarketing(pagePath, pageName)
  }
}

/**
 * Helper function to extract UTM and source parameters from URL
 *
 * @deprecated Use getMarketingParamsWithSource() from @shared/marketing
 * @param searchParams URL search params object (optional, defaults to current window.location)
 */
export function getTrafficSourceParams(searchParams?: URLSearchParams) {
  if (searchParams) {
    const params = extractMarketingParams(searchParams)
    return {
      source: params.source || 'direct',
      ...params,
    }
  }

  // Use the new utility that includes source normalization
  return getMarketingParamsWithSource()
}
